/* Shake to undo (like iPhone): after a swipe-delete, shaking the phone asks "Undo Delete?" (for 30 seconds after the delete).
   It can be switched on or off per device in Settings (localStorage 'ot_shake_enabled' = '1'); OFF by default (owner,
   2026-10-12: iPhone forgets "Allow motion" between launches, so the question kept popping up). When OFF nothing here asks
   for the motion sensor or listens to it: no start-up check, no ask at the next tap, no ask on a swipe-delete, no shake
   handler (the Undo bar still works). Settings → "Shake test" still asks, but only from its Start button.
   When ON:
   iPhone (Safari and the Home Screen app, iOS 13 and later) only lets a page read the motion sensor after asking, and it
   only shows that question from a TAP. A swipe is not a tap for iPhone (it treats a finger that moved like scrolling), so
   asking from a swipe-delete is silently refused. Therefore:
   - turning the switch on (a tap) asks straight away;
   - when the app opens, it quietly checks whether the sensor is already allowed (iPhone answers without a question when
     it still remembers "Allow"); if so, shakes work at once, also right after the first swipe-delete;
   - primeShake() runs inside the tap or swipe that deletes; if iPhone refused because it was a swipe, the next tap
     anywhere in the app asks instead (also at the first tap after opening the app, if this device allowed it before).
   Cancel: nothing is remembered here, so the next delete asks again (iPhone itself may refuse to ask again for a while;
   then nothing happens). Where there is no sensor, nothing happens (the Undo bar still works).
   What counts as a shake: see ../lib/shakeDetect.ts. */
import {shakeDetector} from '../lib/shakeDetect.ts';

const KEY = 'ot_shake';                // 'granted' once allowed on this device (iPhone decides; used to re-ask at the first tap; only when ON)
const ON_KEY = 'ot_shake_enabled';     // '1' = Shake to undo switched on on this device (default off)
let handler: ((ev: Event) => void) | null = null, asking = false, granted = false, askAtNextTap = false, onShake: () => void = () => {};
type DME = typeof DeviceMotionEvent & {requestPermission?: () => Promise<'granted' | 'denied'>};
export type MotionAnswer = 'granted' | 'denied' | 'none' | 'not-a-tap';

export function setShakeHandler(f: () => void) { onShake = f; }
const remember = (v: string) => { try { if (v) localStorage.setItem(KEY, v); else localStorage.removeItem(KEY); } catch { /* private mode */ } };
const remembered = () => { try { return localStorage.getItem(KEY) === 'granted'; } catch { return false; } };
const hasSensorApi = () => typeof window !== 'undefined' && 'DeviceMotionEvent' in window;
const needsAsking = () => typeof (DeviceMotionEvent as DME).requestPermission === 'function';   // iPhone / iPad

/** is Shake to undo switched on on this device? (default off) */
export function shakeEnabled(): boolean { try { return localStorage.getItem(ON_KEY) === '1'; } catch { return false; } }
/** the Settings switch. Call straight inside the tap: switching on asks for the motion sensor right then on iPhone. */
export function setShakeEnabled(on: boolean): Promise<MotionAnswer | 'off'> {
  try { if (on) localStorage.setItem(ON_KEY, '1'); else localStorage.removeItem(ON_KEY); } catch { /* private mode */ }
  if (on) return askMotion();
  stopListening(); granted = false; askAtNextTap = false; remember('');
  return Promise.resolve('off');
}

/** ask for the motion sensor. Must be called straight inside a tap (not after a wait), or iPhone refuses without asking.
    'not-a-tap' = iPhone refused to ask because it did not count as a tap. Listens for shake-to-undo only when switched on
    (the Shake test card asks through here too, but has its own listener). */
export function askMotion(): Promise<MotionAnswer> {
  if (!hasSensorApi()) return Promise.resolve('none');
  if (!needsAsking()) { granted = true; if (shakeEnabled()) listen(); return Promise.resolve('granted'); }   // no question to ask (Android, computer)
  asking = true;
  return (DeviceMotionEvent as DME).requestPermission!().then(r => {
    asking = false;
    if (r === 'granted') { granted = true; askAtNextTap = false; if (shakeEnabled()) { remember('granted'); listen(); } return 'granted' as const; }
    remember(''); return 'denied' as const;                 // Cancel: ask again on the next delete
  }, () => { asking = false; return 'not-a-tap' as const; });
}

/** start listening for shakes (called inside the tap / swipe that deletes); does nothing when switched off */
export function primeShake(): void {
  if (!shakeEnabled() || asking || !hasSensorApi()) return;
  if (granted) { listen(); return; }
  void askMotion().then(r => { if (r === 'not-a-tap' && shakeEnabled()) askAtNextTap = true; });   // a swipe: ask at the next tap instead
}

const vec = (a: DeviceMotionEventAcceleration | null | undefined) => a && a.x != null && a.y != null ? {x: a.x, y: a.y, z: a.z ?? 0} : null;
function listen() {
  if (handler || !shakeEnabled()) return;
  const feed = shakeDetector();
  handler = ev => {
    const e = ev as DeviceMotionEvent, linear = vec(e.acceleration), withGravity = vec(e.accelerationIncludingGravity);
    if (linear || withGravity) { granted = true; askAtNextTap = false; }     // readings arrive: the sensor is allowed
    if (feed({linear, withGravity}, performance.now())) onShake();
  };
  addEventListener('devicemotion', handler);
}
function stopListening() { if (handler) removeEventListener('devicemotion', handler); handler = null; }

/* when the app opens (only when switched on): listen straight away (readings only arrive where the sensor is allowed), and
   on iPhone check quietly whether "Allow" is still remembered; a tap anywhere asks when a swipe could not (see above).
   When off: forget the old "allowed before" memory so nothing asks by itself. */
if (hasSensorApi()) {
  if (!shakeEnabled()) remember('');
  else {
    listen();
    if (needsAsking())
      (DeviceMotionEvent as DME).requestPermission!().then(r => { if (r === 'granted') granted = true; }, () => { if (shakeEnabled() && remembered()) askAtNextTap = true; });
  }
  document.addEventListener('click', () => {
    if (!askAtNextTap || granted || asking || !shakeEnabled() || !needsAsking()) return;
    askAtNextTap = false;                                  // once per refusal, so a refusing iPhone is not asked on every tap
    void askMotion();
  }, true);
}
